import * as React from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { ResidenceCardData, isSearchResult } from 'features/search/types';
import { ImageSizes } from '@organisms/Shell/image-size-enum';
import { getFormattedMessage } from 'features/services/utils/language-helper';
import { getCardUrl, isNotChildSite } from 'features/services/utils/url-helper';

import './style.scss';

import { A } from '@atoms/LinksAndButtons/A';
import { HeadlineLevel, LinkStyle } from 'features/ui/enums';
import { StatusTag } from './_statusTag';
import { AsyncImage } from '@atoms/Media/AsyncImage';
import { capitalizeFirstLetter } from 'features/services/utils/helpers';
import { cleanFinnishNames, getProjectTypeTranslation } from 'features/services/utils/name-helpers';
import { FavoriteButton } from '@atoms/LinksAndButtons/FavoriteButton';
import { ShowingTag } from '@molecules/Showings/ShowingTag';
import { ProjectContent } from './_projectContent';
import { selectShowFloorPlan, useUiStore } from 'features/ui/store/useUiStore';
import { ObjectContent } from './_objectContent';
import { useCurrentTemplate } from 'features/template/ViewModelProvider';
import { TemplateNames } from 'features/template/enums';
import { StageStatus } from 'features/search/filters/enums';
import { ResidenceInterestButton } from './_residenceInterestButton';
import { RentalContent } from './_rentalContent';
import { TagColor, Tags, TagDesign } from '@atoms/Tags/Tags';
import { PremisesContent } from './_premisesContent';
import { ReferenceContent } from './_referenceContent';

export enum ResidenceCardType {
    OBJECT = 'object',
    PROJECT = 'project',
    PURCHASE = 'purchase',
    AREA = 'area',
    REFERENCE = 'reference',
    RENTAL = 'rental',
    PREMISES = 'premises',
}

interface Props {
    residence: ResidenceCardData;
    cardType: ResidenceCardType;
    horizontal ?: boolean;
    onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
    // Semantisk rubriknivå för kortets titel. Sätts av föräldern så kortet ligger ett steg
    // under sektionsrubriken; utelämnad → H4 (utseendet låses till h4-storlek oavsett nivå).
    level?: HeadlineLevel;
    // Filtervärden som kortet inte själv läser: GridedFilterableBlock filtrerar på dem via filterAccessor.
    referenceType?: string;
    municipality?: string;
}

export const ResidenceCard: React.FunctionComponent<Props> = ({
    residence, cardType, horizontal, onClick, level
}) => {
    const url = getCardUrl(residence);
    const currentTemplate = useCurrentTemplate();
    const childSite = isNotChildSite(residence.siteId);
    const showFloorPlan = useUiStore(selectShowFloorPlan);
    let floorplan = cardType === ResidenceCardType.OBJECT && showFloorPlan;
    const showInterestButton = currentTemplate === TemplateNames.CAMPAIGN_PAGE && residence.stageStatus === StageStatus.PLANNED && cardType === ResidenceCardType.PROJECT;

    const getStatusTag = () => {
        switch (cardType) {
            case ResidenceCardType.PROJECT:
                return <StatusTag status={residence.stageStatus} cardType={cardType} />;
            case ResidenceCardType.OBJECT:
                return <StatusTag status={residence.objectStatus} cardType={cardType} />;
            case ResidenceCardType.PURCHASE:
                return <StatusTag status={residence.stageStatus} cardType={cardType} purchase />;
            case ResidenceCardType.PREMISES:
            case ResidenceCardType.RENTAL:
                return <Tags 
                        tagDesign={TagDesign.DEFAULT} 
                        status={residence.status} 
                        className='status-tag'
                    />;
            case ResidenceCardType.REFERENCE:
                return <Tags 
                        tagDesign={TagDesign.DEFAULT} 
                        tagColor={TagColor.GRAY}
                        tagText={residence.types && residence.types.length > 0 ? residence.types[0] : ''}
                        className='status-tag'
                    />;
            default: return;
        }
    };
    const getLinkText = () => {
        switch (cardType) {
            case ResidenceCardType.PROJECT:
                return getFormattedMessage('jm.components.Stage.goToStage', {stageName: capitalizeFirstLetter(cleanFinnishNames(residence.name))});
            case ResidenceCardType.OBJECT:
                return getFormattedMessage('jm.components.Object.goToObject', {objectNumber: residence.apartmentNumber });
            case ResidenceCardType.RENTAL:
                return getFormattedMessage('jm.components.Stage.goToStage', {stageName: residence.displayName });
            case ResidenceCardType.REFERENCE:
                return getFormattedMessage('jm.components.Stage.goToStage', {stageName: residence.displayName});
            case ResidenceCardType.PREMISES:
                return getFormattedMessage('jm.components.Object.goToObject', {objectNumber: residence.apartmentNumber });
            default: return '';
        }
    };

    const cardClasses = classnames('residence-card', {
        'residence-card--horizontal': horizontal,
    });

    return (
        <div className={cardClasses}>
            <div className='residence-card__image'>
                <AsyncImage
                    image={floorplan
                        ? residence.floorPlan
                        : residence.searchImage
                    }
                    quality={ImageSizes.SIZE_600}
                    fullHeight={floorplan || horizontal}
                    fullWidth={!floorplan && !horizontal}
                    alt={(residence.searchImage && !residence.searchImage.altText)
                        ? residence.displayName
                        : ''}
                    noCrop={floorplan}
                />
                {getStatusTag()}
            </div>
            <div className='residence-card__content'>
                {isSearchResult(residence) && <>
                    {(cardType === ResidenceCardType.PROJECT || cardType === ResidenceCardType.PURCHASE) && <ProjectContent residence={residence} level={level} />}
                    {cardType === ResidenceCardType.OBJECT && <ObjectContent residence={residence} level={level} />}
                    
                </>}
                {cardType === ResidenceCardType.RENTAL && <RentalContent residence={residence} level={level} />}
                {cardType === ResidenceCardType.REFERENCE && <ReferenceContent residence={residence} level={level} />}
                {cardType === ResidenceCardType.PREMISES && <PremisesContent premises={residence} level={level} />}
            </div>

            <div className='residence-card__action'>
                {(cardType === ResidenceCardType.PROJECT || cardType === ResidenceCardType.OBJECT) && <FavoriteButton small residence={residence} /> }
                {showInterestButton 
                    ? <ResidenceInterestButton residence={residence} />
                    : <ShowingTag residence={residence} />
                }
                <A
                    href={url}
                    className='residence-card__action__link'
                    variant={LinkStyle.UNSTYLED}
                    route={childSite}
                    target={childSite
                        ? ''
                        : '_blank'}
                    title={getLinkText()}
                    onClick={onClick}
                >
                    <span className='visually-hidden'>
                        {getLinkText()}
                    </span>
                </A>
            </div>
        </div>
    )
};
